import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import Home from "../containers/Home";
import About from "../containers/About";
import Dashboard from "../containers/Dashboard/Dashboard";
import SignIn from "../containers/SignIn/SignIn";
import OTPScreen from "../containers/OTPScreen/OTPScreen";
import RoleSelect from "../containers/RoleSelect/RoleSelect";
import Settings from "../containers/Dashboard/Setting/Settings";
import AuthLogoutListener from "../components/AuthLogoutListener";
import WebAppRouter from "../srcWeb/router/WebAppRouter";
import Rooms from "../srcWeb/containers/Rooms";
import FeatureScreen from "../srcWeb/containers/FeatureScreen/FeatureScreen";
import Doodling from "../srcWeb/containers/Doodling/Doodling";
import Jokes from "../srcWeb/containers/Jokes/Jokes";
import BreatheExcercise from "../srcWeb/containers/BreatheExcercise/BreatheExcercise";
import GrownupScreen from "../srcWeb/containers/BreatheExcercise/components/GrownupScreen/GrownupScreen";
import KidsJournal from "../srcWeb/containers/KidsJournal/KidsJournal";
import ChatBoxScreen from "../srcWeb/containers/ChatBoxScreen/ChatBoxScreen";
import MagicBagPack from "../srcWeb/containers/MagicBagPack/MagicBagPack";
import QuestScreen from "../srcWeb/containers/QuestScreen/QuestScreen";
import AppJourneyPage from "../srcWeb/containers/AppJourneyPage";
import AppStoryScreen from "../srcWeb/containers/WebStoryScreen";
import WebStoryBreakScreen from "../srcWeb/containers/WebStoryScreen/Components/WebStoryBreakScreen";
import Resources from "../containers/Resources/Resources";

interface AppRouterProps {
  isAuthenticated: boolean;
  setIsAuthenticated: (value: boolean) => void;
  needsOTPVerification: boolean;
  setNeedsOTPVerification: (value: boolean) => void;
  needsRoleSelection: boolean;
  setNeedsRoleSelection: (value: boolean) => void;
  userEmail: string;
  setUserEmail: (value: string) => void;
}

const WEB_TITLE = "Experience Troo";
const WEB_DESCRIPTION = "A Facilitative-AI SEL Platform";
const DASHBOARD_TITLE = "Troo Dashboard";
const DASHBOARD_DESCRIPTION = "The Comprehensive SEL Platform";

const DocumentMeta = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const isWebApp = pathname.startsWith("/web");
    document.title = isWebApp ? WEB_TITLE : DASHBOARD_TITLE;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        isWebApp ? WEB_DESCRIPTION : DASHBOARD_DESCRIPTION,
      );
    }
  }, [pathname]);
  return null;
};

const getPostLoginPath = () =>
  localStorage.getItem("selectedMode") === "webapp"
    ? "/web/rooms"
    : "/dashboard";

const AppRouter = ({
  isAuthenticated,
  setIsAuthenticated,
  needsOTPVerification,
  setNeedsOTPVerification,
  needsRoleSelection,
  setNeedsRoleSelection,
  userEmail,
  setUserEmail,
}: AppRouterProps) => {
  return (
    <BrowserRouter>
      <DocumentMeta />
      {/* Central listener to handle logout events from API clients */}
      <AuthLogoutListener setIsAuthenticated={setIsAuthenticated} />
      {/* {isAuthenticated && <Navbar />} */}
      <Routes>
        {!isAuthenticated ? (
          <>
            <Route
              path="/signin"
              element={
                <SignIn
                  setIsAuthenticated={setIsAuthenticated}
                  setNeedsOTPVerification={setNeedsOTPVerification}
                  setUserEmail={setUserEmail}
                />
              }
            />
            <Route
              path="/verify-otp"
              element={
                needsOTPVerification ? (
                  <OTPScreen
                    setIsAuthenticated={setIsAuthenticated}
                    setNeedsRoleSelection={setNeedsRoleSelection}
                    email={userEmail}
                  />
                ) : (
                  <Navigate to="/signin" replace />
                )
              }
            />
            <Route
              path="/role-select"
              element={
                needsRoleSelection ? (
                  <RoleSelect setIsAuthenticated={setIsAuthenticated} />
                ) : (
                  <Navigate to="/signin" replace />
                )
              }
            />
            <Route path="*" element={<Navigate to="/signin" replace />} />
          </>
        ) : (
          <>
            <Route index element={<Dashboard />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/dashboard/:tab" element={<Dashboard />} />
            <Route
              path="/"
              element={<Navigate to={getPostLoginPath()} replace />}
            />
            <Route path="/home" element={<Home />} />
            <Route path="/about" element={<About />} />
            {/* Full-screen immersive routes — BEFORE /web/* so they bypass WebLayout/WebHeader */}
            <Route path="/web/rooms" element={<Rooms />} />

            {/* Room object navigation targets — placeholders until real screens are built */}
            <Route path="/web/breathe" element={<BreatheExcercise />} />
            <Route path="/web/grownup" element={<GrownupScreen />} />
            <Route path="/web/journal" element={<KidsJournal />} />
            <Route
              path="/web/planner"
              element={
                <FeatureScreen
                  title="Travel Planner"
                  icon="🎁"
                  description="Plan your next adventure."
                />
              }
            />
            <Route
              path="/web/my-wall"
              element={
                <FeatureScreen
                  title="My Wall"
                  icon="🖼️"
                  description="Your collection of memories."
                />
              }
            />
            <Route path="/web/magic-backpack" element={<MagicBagPack />} />
            <Route path="/web/quest" element={<QuestScreen />} />
            <Route path="/web/chatbox" element={<ChatBoxScreen />} />
            <Route path="/web/doodling" element={<Doodling />} />
            <Route path="/web/jokes" element={<Jokes />} />
            <Route
              path="/web/star-board"
              element={
                <FeatureScreen
                  title="Star Board"
                  icon="⭐"
                  description="See your published work."
                />
              }
            />
            <Route path="/web/appJourneyPage" element={<AppJourneyPage />} />
            <Route path="/web/appStoryScreen" element={<AppStoryScreen />} />
            <Route
              path="/web/WebStoryBreakScreen"
              element={<WebStoryBreakScreen />}
            />
            <Route path="/web/*" element={<WebAppRouter />} />
            <Route
              path="*"
              element={<Navigate to={getPostLoginPath()} replace />}
            />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
            <Route path="/resources" element={<Resources />} />
          </>
        )}
      </Routes>
    </BrowserRouter>
  );
};

export default AppRouter;
